import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useStartAllocationFlow } from "Order/Api/Mutation/useStartAllocationFlowMutation";
import { PlayArrow } from "@mui/icons-material";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from "@mui/material";
import { useState } from "react";

type StartAllocationFlowButtonProps = {
	orderId: number;
};

const allocationResource = Resources.base.order.allocationFlow;

export function StartAllocationFlowButton({
	orderId,
}: StartAllocationFlowButtonProps) {
	const { t } = useResource();
	const [modal, setModal] = useState<"start" | "reallocate" | null>(null);
	const { mutateAsync: startAllocationFlow, isPending } =
		useStartAllocationFlow(orderId);
	const hasUpdatePermission = hasPermission(UserPermissions.OrderModule.update);

	if (!hasUpdatePermission) {
		return null;
	}

	const handleClose = () => {
		if (!isPending) {
			setModal(null);
		}
	};

	const handleStart = async () => {
		const response = await startAllocationFlow({
			orderId,
			forceReallocation: false,
		});

		if (response.status === "already_allocated") {
			setModal("reallocate");
			return;
		}

		setModal(null);
	};

	const handleReallocate = async () => {
		await startAllocationFlow({
			orderId,
			forceReallocation: true,
		});
		setModal(null);
	};

	return (
		<>
			<Button
				size="small"
				variant="contained"
				startIcon={<PlayArrow />}
				onClick={() => setModal("start")}
				loading={isPending}
			>
				{t(allocationResource.startButton)}
			</Button>
			<Dialog open={modal === "start"} onClose={handleClose}>
				<DialogTitle>{t(allocationResource.startTitle)}</DialogTitle>
				<DialogContent>{t(allocationResource.startDescription)}</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={handleClose}>
						{t(allocationResource.cancel)}
					</Button>
					<Button variant="contained" onClick={handleStart} loading={isPending}>
						{t(allocationResource.confirm)}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={modal === "reallocate"} onClose={handleClose}>
				<DialogTitle>{t(allocationResource.reallocateTitle)}</DialogTitle>
				<DialogContent>
					{t(allocationResource.reallocateDescription)}
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={handleClose}>
						{t(allocationResource.cancel)}
					</Button>
					<Button
						variant="contained"
						color="warning"
						onClick={handleReallocate}
						loading={isPending}
					>
						{t(allocationResource.reallocateConfirm)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
